import {  Box, Grid, IconButton, List, ListItem, ListItemText, TextField, Typography  } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { ChevronDown, Download } from "lucide-react";
import React from "react";
import { CommonWidgetContainer } from "../../components/Commons/CommonWidgetContainer.styles";
import { Icon } from "../../components/Icon/Icon.component";
import { VisuallyHiddenInput } from "../../components/InputCst/UploadInput";
import {  FormValues, Errors  } from "../../routes/Profile/AddSkillBook/AddSkillBook.types";
import { handleDownloadFile } from "../../utils/skillBook";

interface DettagliDidatticiSectionProps {
  expanded: boolean;
  onToggle: () => void;
  formValues: FormValues;
  setFormValues: React.Dispatch<React.SetStateAction<FormValues>>;
  errors: Errors;
  t: (key: string) => string;
  readOnly?: boolean;
}

const DettagliDidatticiSection: React.FC<DettagliDidatticiSectionProps> = ({
  expanded,
  onToggle,
  formValues,
  setFormValues,
  errors,
  t,
  readOnly
}) => {
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const {  name, value  } = e.target;
    setFormValues((prev) => ({  ...prev, [name]: value  }));
  };
  const theme = useTheme();

  const MAX_SIZE_UPLOAD = 10 * 1024 * 1024; // 10 Mb

  const handleMultiFileChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    name: string,
    maxFileSize: number
  ) => {
    if (e.target.files) {
      try {
        let newErrors: Errors = { ...errors };
        const files = Array.from(e.target.files);
        const validFiles: File[] = [];
  
        const allowedTypes = ["application/pdf", "image/jpeg", "image/png",   
          "text/csv", "application/vnd.ms-excel", "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", 
          "application/msword", "application/vnd.openxmlformats-officedocument.wordprocessingml.document"];
  
        for(const file of files) {
          if (!allowedTypes.includes(file.type)) { //CHECK FILE TYPE
            newErrors["attachments"] = "file_format_error";
            errors = newErrors;
            return;
          } else if (file.size > maxFileSize) { //CHECK FILE SIZE
            newErrors["attachments"] = t("upload_file_size_error");
            errors = newErrors;
            return;
          }
          validFiles.push(file);
        }
  
        delete newErrors["attachments"];
        errors = newErrors;
        setFormValues((prevValues) => ({
          ...prevValues,
          [name]: [...(Array.isArray(prevValues[name]) ? (prevValues[name] as File[]) : []), ...validFiles]
        }));
  
      } catch (error) {
        console.log("Error: ", error);
      }
    }
  };

  const handleMultiDelete = (
    name: string,
    fileName: string
  ) => {
    setFormValues(prevValues => ({
      ...prevValues,
      [name]: Array.isArray(prevValues[name])
        ? (prevValues[name] as File[]).filter((file: File) => file.name !== fileName)
        : []
    }));
  };

  return (
    <CommonWidgetContainer
      sx={ {
        width: "100%",
        maxWidth: "100%",
        borderRadius: "12px",
        mb: 2,
        p: 2
      } }
    >
      <Box
        onClick={ onToggle }
        aria-pressed={ expanded }
        sx={ {
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          cursor: "pointer"
        } }
      >
        <Typography sx={ {  fontWeight: "bold", color: theme.customColors.textPrimary, fontSize: "1.2rem"  } }>
          { t("detail_section") }
        </Typography>
        <ChevronDown
          color={ theme.customColors.accentVariantA }
          width="2rem"
          height="2rem"
          style={ {
            transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform .4s linear",
            color: theme.customColors.borderAccent
          } }
        />
      </Box>

      { expanded && (
        <Grid container spacing={ 2 }>
          <Grid item xs={ 12 } sm={ 6 }>
            <p style={ {  fontSize: "10px"  } }>{ t("teacher_field") }</p>
            <TextField
              fullWidth
              name="teacher"
              label="-"
              value={ formValues.teacher || "" }
              onChange={ handleInputChange }
              error={ !!errors.teacher }
              helperText={ errors.teacher }
              disabled={ readOnly }
              sx={ {
                "& .MuiInputBase-input.Mui-disabled": {
                  WebkitTextFillColor: theme.customColors.textDisabledAlternative, // testo disabilitato
                  opacity: 1
                },
                "& .MuiOutlinedInput-root": {
                  backgroundColor: theme.customColors.backgroundPrimary,
                  color: theme.customColors.textPrimary,
                  borderRadius: "8px",
                  "& fieldset": {
                    borderColor: theme.customColors.border
                  }
                },
                "& .MuiInputLabel-root": {
                  color: theme.customColors.textPrimary,
                  backgroundColor: theme.customColors.backgroundPrimary,
                  borderRadius: "8px",
                  paddingX: 0.5
                }
              } }
            />
          </Grid>

          <Grid item xs={ 12 } sm={ 6 }>
            <p style={ {  fontSize: "10px"  } }>{ t("plan_field") }</p>
            <TextField
              fullWidth
              name="plan"
              label="-"
              value={ formValues.plan || "" }
              onChange={ handleInputChange }
              error={ !!errors.plan }
              helperText={ errors.plan }
              disabled={ readOnly }
              sx={ {
                "& .MuiInputBase-input.Mui-disabled": {
                  WebkitTextFillColor: theme.customColors.textDisabledAlternative, // testo disabilitato
                  opacity: 1
                },
                "& .MuiOutlinedInput-root": {
                  backgroundColor: theme.customColors.backgroundPrimary,
                  color: theme.customColors.textPrimary,
                  borderRadius: "8px",
                  "& fieldset": {
                    borderColor: theme.customColors.border
                  }
                },
                "& .MuiInputLabel-root": {
                  color: theme.customColors.textPrimary,
                  backgroundColor: theme.customColors.backgroundPrimary,
                  borderRadius: "8px",
                  paddingX: 0.5
                }
              } }
            />
          </Grid>

          <Grid item xs={ 12 } sm={ 12 }>
            <p style={ {  fontSize: "10px"  } }>{ t("durationParticipation_field") }</p>
            <TextField
              fullWidth
              name="durationParticipation"
              label="-"
              value={ formValues.durationParticipation || "" }
              onChange={ handleInputChange }
              error={ !!errors.durationParticipation }
              helperText={ errors.durationParticipation }
              disabled={ readOnly }
              sx={ {
                "& .MuiInputBase-input.Mui-disabled": {
                  WebkitTextFillColor: theme.customColors.textDisabledAlternative, // testo disabilitato
                  opacity: 1
                },
                "& .MuiOutlinedInput-root": {
                  backgroundColor: theme.customColors.backgroundPrimary,
                  color: theme.customColors.textPrimary,
                  borderRadius: "8px",
                  "& fieldset": {
                    borderColor: theme.customColors.border
                  }
                },
                "& .MuiInputLabel-root": {
                  color: theme.customColors.textPrimary,
                  backgroundColor: theme.customColors.backgroundPrimary,
                  borderRadius: "8px",
                  paddingX: 0.5
                }
              } }
            />
          </Grid>

          <Grid item xs={ 12 } sm={ 12 }>
            <p style={ {  fontSize: "10px"  } }>{ t("attachments_field") }</p>
            <VisuallyHiddenInput
              name={ "attachments" }
              label={ "" }
              type="file"
              accept=".pdf, .jpg, .jpeg, .png, .csv, .xls, .xlsx, .doc, .docx"
              onChange={ (e) => {
                handleMultiFileChange(
                  e,
                  "attachments",
                  MAX_SIZE_UPLOAD
                );
              } }
              error={ !!errors["attachments"] }
              errorMessage={ t(`${errors["attachments"]}`) }
              readOnly = { readOnly }
            />
            <List dense>
              { Array.isArray(formValues.attachments) &&
                formValues.attachments.map((file, index) => (
                  <ListItem
                    key={ index }
                    sx={ {
                      border: "1px solid #ccc",
                      borderRadius: "12px"
                    } }
                    secondaryAction={
                      <>
                        <IconButton
                          edge="end"
                          aria-label="open"
                          onClick={ () => {
                            handleDownloadFile(file);
                          } }
                        >
                          <Download
                            className="icon icon-download"
                            width={ "20px" }
                            height={ "20px" }
                            color={ theme.customColors.accentVariantA }
                          />
                        </IconButton>

                        <IconButton
                          edge="end"
                          aria-label="delete"
                          onClick={ () => {
                            handleMultiDelete(
                              "attachments",
                              file.name
                            );
                          } }
                          disabled={ readOnly }
                        >
                          <Icon
                            icon={ "Icons_delete" }
                            size={ 20 }
                          />
                        </IconButton>

                      </>
                    }
                  >
                    <ListItemText primary={ file.name } />
                  </ListItem>
                )) }
            </List>
          </Grid>
        </Grid>
      ) }
    </CommonWidgetContainer>
  );
};

export default DettagliDidatticiSection;
